import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError, fetchMe, fetchReadiness, logout, type UserProfile } from './api'
import AuditPage from './AuditPage'
import DepartmentsPage from './DepartmentsPage'
import LoginPage from './LoginPage'
import OrgChart from './OrgChart'
import ProcessesPage from './ProcessesPage'
import UsersPage from './UsersPage'

type Tab = 'chart' | 'processes' | 'departments' | 'users' | 'audit'

function ServiceStatus() {
  const readiness = useQuery({ queryKey: ['ready'], queryFn: fetchReadiness, retry: false })

  if (readiness.data?.status === 'ok') {
    return <span className="good">БД подключена ({readiness.data.database.dialect})</span>
  }
  if (readiness.data?.status === 'degraded') {
    return (
      <span className="bad">
        БД недоступна — {readiness.data.database.error ?? 'причина не указана'}
      </span>
    )
  }
  if (readiness.isError) {
    return <span className="bad">Нет ответа от API</span>
  }
  return <span className="muted">проверяем…</span>
}

function Header({ user }: { user: UserProfile }) {
  const queryClient = useQueryClient()

  const signOut = useMutation({
    mutationFn: logout,
    onSuccess: () => queryClient.setQueryData(['me'], null),
  })

  return (
    <header className="topbar">
      <div>
        <strong>Оргструктура и бизнес-процессы</strong>
        <div className="topbar-status">
          <ServiceStatus />
        </div>
      </div>
      <div className="topbar-user">
        <span>{user.email}</span>
        <button type="button" onClick={() => signOut.mutate()} disabled={signOut.isPending}>
          Выйти
        </button>
      </div>
    </header>
  )
}

export default function App() {
  const [tab, setTab] = useState<Tab>('chart')
  const me = useQuery({
    queryKey: ['me'],
    queryFn: fetchMe,
    retry: false,
    // 401 — обычное состояние «не вошёл», а не сбой: показываем форму входа.
    throwOnError: false,
  })

  if (me.isPending) {
    return (
      <main className="narrow">
        <p className="muted">Загружаем…</p>
      </main>
    )
  }

  const unauthenticated = me.error instanceof ApiError && me.error.status === 401
  if (unauthenticated || !me.data) {
    return <LoginPage />
  }

  return <Workspace tab={tab} setTab={setTab} user={me.data} />
}

function Workspace({
  tab,
  setTab,
  user,
}: {
  tab: Tab
  setTab: (tab: Tab) => void
  user: UserProfile
}) {
  const isAdmin = user.role === 'admin'

  return (
    <>
      <Header user={user} />
      <main className={tab === 'chart' ? 'wide' : undefined}>
        <nav className="tabs">
          <button
            type="button"
            className={tab === 'chart' ? 'tab active' : 'tab'}
            onClick={() => setTab('chart')}
          >
            Оргструктура
          </button>
          {/* Процессы видны всем ролям: сотрудник получает только те, где назван лично,
              и фильтрует их сервер, а не эта вкладка. */}
          <button
            type="button"
            className={tab === 'processes' ? 'tab active' : 'tab'}
            onClick={() => setTab('processes')}
          >
            Бизнес-процессы
          </button>
          {isAdmin && (
            <button
              type="button"
              className={tab === 'departments' ? 'tab active' : 'tab'}
              onClick={() => setTab('departments')}
            >
              Подразделения
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className={tab === 'users' ? 'tab active' : 'tab'}
              onClick={() => setTab('users')}
            >
              Аккаунты
            </button>
          )}
          {isAdmin && (
            <button
              type="button"
              className={tab === 'audit' ? 'tab active' : 'tab'}
              onClick={() => setTab('audit')}
            >
              Журнал изменений
            </button>
          )}
        </nav>

        {tab === 'chart' && <OrgChart currentUser={user} />}
        {tab === 'processes' && <ProcessesPage currentUser={user} />}
        {isAdmin && tab === 'departments' && <DepartmentsPage />}
        {isAdmin && tab === 'users' && <UsersPage currentUser={user} />}
        {isAdmin && tab === 'audit' && <AuditPage />}
      </main>
    </>
  )
}
